import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Divider,
  Alert,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
  Tooltip,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CustomButton from "../../Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PhoneIcon from "@mui/icons-material/Phone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoIcon from "@mui/icons-material/Info";
import PersonIcon from "@mui/icons-material/Person";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ScheduleIcon from "@mui/icons-material/Schedule";
import SettingsIcon from "@mui/icons-material/Settings";
import apiClient from "../../../services/apiClient";
import toast from "react-hot-toast";

const STEPS = ["Campaign Details", "Recipients", "Configuration", "Schedule & Launch"];

const OutboundCampaignModal = ({ open, onClose, data, onSave }) => {
  const isEdit = !!data;
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});

  // Campaign Types State
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [loadingCampaignTypes, setLoadingCampaignTypes] = useState(false);
  const [selectedTypeDescription, setSelectedTypeDescription] = useState("");

  // Assistants State
  const [assistants, setAssistants] = useState([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    assistant: "",
    phoneNumber: "",
    contactList: null,
    contactListName: "",
    totalContacts: 0,
    csvVariables: [],
    schedule: "Immediately",
    scheduleDate: "",
    scheduleTime: "",
    maxConcurrentCalls: 5,
    callsPerHour: 100,
    retryAttempts: 2,
    retryDelay: 30,
    workingHours: {
      enabled: false,
      startTime: "09:00",
      endTime: "17:00",
    },
    callScript: "",
  });

  // Fetch campaign types and assistants when modal opens
  useEffect(() => {
    if (open) {
      fetchCampaignTypes();
      fetchAssistants();
    }
  }, [open]);

  // Populate form data in edit mode
  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || "",
        description: data.description || "",
        type: data.type || "",
        assistant: data.assistant || "",
        phoneNumber: data.phoneNumber || "",
        contactList: null,
        contactListName: data.contactListName || "",
        totalContacts: data.contacts || 0,
        csvVariables: data.csvVariables || [],
        schedule: data.schedule || "Immediately",
        scheduleDate: data.scheduleDate || "",
        scheduleTime: data.scheduleTime || "",
        maxConcurrentCalls: data.maxConcurrentCalls || 5,
        callsPerHour: data.callsPerHour || 100,
        retryAttempts: data.retryAttempts || 2,
        retryDelay: data.retryDelay || 30,
        workingHours: data.workingHours || {
          enabled: false,
          startTime: "09:00",
          endTime: "17:00",
        },
        callScript: data.callScript || "",
      });

      // Set description for the selected type in edit mode
      if (data.type && campaignTypes.length > 0) {
        const selectedType = campaignTypes.find((ct) => ct.type === data.type);
        if (selectedType) {
          setSelectedTypeDescription(selectedType.description);
        }
      }
    }
  }, [data, campaignTypes]);

  // Update description when type changes
  useEffect(() => {
    if (formData.type && campaignTypes.length > 0) {
      const selectedType = campaignTypes.find((ct) => ct.type === formData.type);
      setSelectedTypeDescription(selectedType?.description || "");
    } else {
      setSelectedTypeDescription("");
    }
  }, [formData.type, campaignTypes]);

  // Fetch campaign types from API
  const fetchCampaignTypes = async () => {
    setLoadingCampaignTypes(true);
    try {
      const res = await apiClient.get("/campaign-types/dropdown");
      if (res?.status && Array.isArray(res.data)) {
        setCampaignTypes(res.data);
      } else {
        toast.error("Failed to load campaign types");
        // Set default fallback types
        setCampaignTypes([
          { type: "Sales & Lead Generation", description: "Default sales campaigns" },
          { type: "Customer Survey", description: "Default survey campaigns" },
        ]);
      }
    } catch (err) {
      console.error("Error fetching campaign types:", err);
      toast.error("Error fetching campaign types");
    } finally {
      setLoadingCampaignTypes(false);
    }
  };

  // Fetch assistants from API
  const fetchAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const res = await apiClient.get("/assistants-dropdown");
      if (res?.status && Array.isArray(res.data)) {
        setAssistants(res.data);
      } else {
        toast.error("Failed to load assistants");
        setAssistants([]);
      }
    } catch (err) {
      console.error("Error fetching assistants:", err);
      toast.error("Error fetching assistants");
      setAssistants([]);
    } finally {
      setLoadingAssistants(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx')) {
        setErrors({ ...errors, contactList: "Please upload a CSV or XLSX file" });
        return;
      }

      // Simulate parsing CSV and extracting variables
      const contactCount = Math.floor(Math.random() * 900) + 100;
      const variables = ["name", "company", "email", "customer_issue"];

      setFormData({
        ...formData,
        contactList: file,
        contactListName: file.name,
        totalContacts: contactCount,
        csvVariables: variables,
      });
      setErrors({ ...errors, contactList: null });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 0) {
      if (!formData.name.trim()) {
        newErrors.name = "Campaign name is required";
      }
      if (!formData.type) {
        newErrors.type = "Campaign type is required";
      }
    }

    if (step === 1) {
      if (!formData.contactListName) {
        newErrors.contactList = "Please upload a contact list";
      }
      if (formData.totalContacts === 0) {
        newErrors.contactList = "Contact list must contain at least one contact";
      }
    }

    if (step === 2) {
      if (!formData.assistant) {
        newErrors.assistant = "Please select an assistant";
      }
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = "Please select a phone number";
      }
    }

    if (step === 3) {
      if (formData.schedule === "Scheduled") {
        if (!formData.scheduleDate) {
          newErrors.scheduleDate = "Please select a start date";
        }
        if (!formData.scheduleTime) {
          newErrors.scheduleTime = "Please select a start time";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    try {
      // Get campaign type description
      const selectedCampaignType = campaignTypes.find((ct) => ct.type === formData.type);
      const campaignTypeDescription = selectedCampaignType?.description || "";

      // Transform formData to match API structure
      const payload = {
        campaign_name: formData.name,
        campaign_type: formData.type,
        contact_file: formData.contactListName,
        total_contacts: formData.totalContacts,
        ai_assistant: formData.assistant,
        caller_id: formData.phoneNumber,
        call_rate: formData.callsPerHour,
        max_concurrent: formData.maxConcurrentCalls,
        retry_attempts: formData.retryAttempts,
        retry_delay: formData.retryDelay,
        schedule_type: formData.schedule === "Immediately" ? "Start Immediately" : "Scheduled",
        start_date: formData.scheduleDate ? new Date(formData.scheduleDate).toISOString() : new Date(0).toISOString(),
        start_time: formData.scheduleTime ? new Date(`1970-01-01T${formData.scheduleTime}`).toISOString() : new Date(0).toISOString(),
        additional_instructions: formData.callScript,
        campaignType_description: campaignTypeDescription,
      };

      // Make API call to create campaign
      const response = await apiClient.post("/campaigns", payload);

      if (response?.status) {
        toast.success(isEdit ? "Campaign updated successfully" : "Campaign created successfully");
        // Call onSave to trigger refresh in parent component
        if (onSave) {
          onSave(response.data);
          // Don't call handleClose() here - let ModalManager handle it after refresh
        } else {
          handleClose();
        }
      } else {
        toast.error(response?.message || "Failed to create campaign");
      }
    } catch (err) {
      console.error("Error creating campaign:", err);
      toast.error(err?.response?.data?.message || "Error creating campaign");
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setErrors({});
    setFormData({
      name: "",
      description: "",
      type: "",
      assistant: "",
      phoneNumber: "",
      contactList: null,
      contactListName: "",
      totalContacts: 0,
      csvVariables: [],
      schedule: "Immediately",
      scheduleDate: "",
      scheduleTime: "",
      maxConcurrentCalls: 5,
      callsPerHour: 100,
      retryAttempts: 2,
      retryDelay: 30,
      workingHours: {
        enabled: false,
        startTime: "09:00",
        endTime: "17:00",
      },
      callScript: "",
    });
    setSelectedTypeDescription("");
    onClose();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              Create automated outbound calling campaigns to reach multiple contacts efficiently. Start by defining your campaign details.
            </Alert>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" color="primary" />
                Campaign Information
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  error={!!errors.name}
                  helperText={errors.name || "Give your campaign a descriptive name"}
                  placeholder="e.g., Q1 Customer Survey"
                />

                <FormControl fullWidth error={!!errors.type}>
                  <InputLabel>Campaign Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Campaign Type"
                    onChange={(e) => handleChange("type", e.target.value)}
                    disabled={loadingCampaignTypes}
                    endAdornment={
                      loadingCampaignTypes && (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      )
                    }
                  >
                    {loadingCampaignTypes ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          Loading campaign types...
                        </Typography>
                      </MenuItem>
                    ) : campaignTypes.length > 0 ? (
                      campaignTypes.map((campaignType) => (
                        <MenuItem key={campaignType._id} value={campaignType.type}>
                          {campaignType.type}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          No campaign types available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                  {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
                </FormControl>

                {/* Show description for selected campaign type */}
                {selectedTypeDescription && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      <strong>About this type:</strong> {selectedTypeDescription}
                    </Typography>
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Brief description of the campaign purpose and goals"
                />
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              Upload a CSV file with your contact list. Required column: <strong>number</strong> (in E.164 format: +[country code][phone number])
            </Alert>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon fontSize="small" color="primary" />
                Recipients
              </Typography>

              <Card
                sx={{
                  mt: 2,
                  border: errors.contactList ? "2px dashed #d32f2f" : "2px dashed #E0E0E0",
                  backgroundColor: "#F9FAFB",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "#F3F4F6",
                  }
                }}
                onClick={() => document.getElementById("contact-list-upload").click()}
              >
                <CardContent>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 2 }}>
                    <PhoneIcon sx={{ fontSize: 48, color: formData.contactListName ? "success.main" : "primary.main" }} />
                    <input
                      accept=".csv,.xlsx"
                      style={{ display: "none" }}
                      id="contact-list-upload"
                      type="file"
                      onChange={handleFileUpload}
                    />
                    <CustomButton
                      icon={<CloudUploadIcon />}
                      iconPosition="left"
                      text={formData.contactListName ? "Change Contact List" : "Upload Contact List"}
                      component="span"
                      variant={formData.contactListName ? "outlined" : "contained"}
                    />
                    {formData.contactListName ? (
                      <Box sx={{ textAlign: "center", width: "100%" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mb: 1 }}>
                          <CheckCircleIcon color="success" />
                          <Typography variant="body1" fontWeight={600}>
                            {formData.contactListName}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${formData.totalContacts} contacts loaded`}
                          color="success"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Click to browse or drag and drop your CSV/XLSX file
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>

              {errors.contactList && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                  {errors.contactList}
                </Typography>
              )}

              {formData.csvVariables.length > 0 && (
                <Paper sx={{ mt: 3, p: 2, backgroundColor: "#F0F9FF" }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Dynamic Variables Detected
                  </Typography>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    These variables can be used in your assistant prompt with {"{{"} {"}}"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
                    {formData.csvVariables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                </Paper>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>CSV Format Requirements:</strong>
                  <br />• Required column: <code>number</code> (in E.164 format: +1234567890)
                  <br />• Optional columns can be used as dynamic variables
                  <br />• Column names must not contain spaces
                  <br />• First row should contain column headers
                </Typography>
              </Alert>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              Configure your AI assistant and calling settings for this campaign.
            </Alert>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SmartToyIcon fontSize="small" color="primary" />
                AI Assistant Configuration
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <FormControl fullWidth error={!!errors.assistant}>
                  <InputLabel>Select Assistant *</InputLabel>
                  <Select
                    value={formData.assistant}
                    label="Select Assistant *"
                    onChange={(e) => handleChange("assistant", e.target.value)}
                    disabled={loadingAssistants}
                    endAdornment={
                      loadingAssistants && (
                        <CircularProgress size={20} sx={{ mr: 2 }} />
                      )
                    }
                  >
                    {loadingAssistants ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          Loading assistants...
                        </Typography>
                      </MenuItem>
                    ) : assistants.length > 0 ? (
                      assistants.map((assistant) => (
                        <MenuItem key={assistant._id} value={assistant._id}>
                          <Typography variant="body2" fontWeight={600}>
                            {assistant.name}
                          </Typography>
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          No assistants available
                        </Typography>
                      </MenuItem>
                    )}
                  </Select>
                  {errors.assistant && <FormHelperText>{errors.assistant}</FormHelperText>}
                </FormControl>

                <FormControl fullWidth error={!!errors.phoneNumber}>
                  <InputLabel>Caller ID (Phone Number) *</InputLabel>
                  <Select
                    value={formData.phoneNumber}
                    label="Caller ID (Phone Number) *"
                    onChange={(e) => handleChange("phoneNumber", e.target.value)}
                  >
                    <MenuItem value="+1 (555) 123-4567">+1 (555) 123-4567 - Main Line</MenuItem>
                    <MenuItem value="+1 (555) 234-5678">+1 (555) 234-5678 - Sales</MenuItem>
                    <MenuItem value="+1 (555) 345-6789">+1 (555) 345-6789 - Support</MenuItem>
                  </Select>
                  {errors.phoneNumber && <FormHelperText>{errors.phoneNumber}</FormHelperText>}
                  <FormHelperText>This number will appear on recipients' caller ID</FormHelperText>
                </FormControl>

                <TextField
                  fullWidth
                  label="Additional Instructions (Optional)"
                  value={formData.callScript}
                  onChange={(e) => handleChange("callScript", e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Additional context or instructions for the AI assistant during calls..."
                  helperText="Use dynamic variables from your CSV like {{name}} or {{company}}"
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SettingsIcon fontSize="small" color="primary" />
                Call Settings
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <TextField
                  fullWidth
                  label="Calls Per Hour"
                  type="number"
                  value={formData.callsPerHour}
                  onChange={(e) => handleChange("callsPerHour", parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 1, max: 500 } }}
                  helperText="Rate limit for outbound calls"
                />

                <TextField
                  fullWidth
                  label="Max Concurrent Calls"
                  type="number"
                  value={formData.maxConcurrentCalls}
                  onChange={(e) => handleChange("maxConcurrentCalls", parseInt(e.target.value) || 0)}
                  InputProps={{ inputProps: { min: 1, max: 50 } }}
                  helperText="Maximum number of simultaneous calls"
                />

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Retry Attempts"
                    type="number"
                    value={formData.retryAttempts}
                    onChange={(e) => handleChange("retryAttempts", parseInt(e.target.value) || 0)}
                    InputProps={{ inputProps: { min: 0, max: 5 } }}
                    helperText="Failed call retries"
                  />
                  <TextField
                    fullWidth
                    label="Retry Delay (minutes)"
                    type="number"
                    value={formData.retryDelay}
                    onChange={(e) => handleChange("retryDelay", parseInt(e.target.value) || 0)}
                    InputProps={{ inputProps: { min: 5, max: 120 } }}
                    helperText="Wait time between retries"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Alert severity="success" icon={<CheckCircleIcon />}>
              Review your campaign settings and schedule when to start calling.
            </Alert>

            <Paper sx={{ p: 3, backgroundColor: "#F9FAFB" }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Campaign Summary
              </Typography>
              <Divider sx={{ my: 2 }} />

              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Campaign Name"
                    secondary={formData.name}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Type"
                    secondary={formData.type}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Total Contacts"
                    secondary={`${formData.totalContacts} contacts from ${formData.contactListName}`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="AI Assistant"
                    secondary={
                      assistants.find((a) => a._id === formData.assistant)?.name || formData.assistant
                    }
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Caller ID"
                    secondary={formData.phoneNumber}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Call Rate"
                    secondary={`${formData.callsPerHour} calls/hour, max ${formData.maxConcurrentCalls} concurrent`}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
                  />
                </ListItem>
              </List>
            </Paper>

            <Box>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ScheduleIcon fontSize="small" color="primary" />
                Schedule Campaign
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>When to Start</InputLabel>
                  <Select
                    value={formData.schedule}
                    label="When to Start"
                    onChange={(e) => handleChange("schedule", e.target.value)}
                  >
                    <MenuItem value="Immediately">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Start Immediately</Typography>
                        <Typography variant="caption" color="text.secondary">Begin calling as soon as campaign is created</Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value="Scheduled">
                      <Box>
                        <Typography variant="body2" fontWeight={600}>Schedule for Later</Typography>
                        <Typography variant="caption" color="text.secondary">Set a specific date and time to start</Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {formData.schedule === "Scheduled" && (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Start Date"
                      type="date"
                      value={formData.scheduleDate}
                      onChange={(e) => handleChange("scheduleDate", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.scheduleDate}
                      helperText={errors.scheduleDate}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    />
                    <TextField
                      fullWidth
                      label="Start Time"
                      type="time"
                      value={formData.scheduleTime}
                      onChange={(e) => handleChange("scheduleTime", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.scheduleTime}
                      helperText={errors.scheduleTime}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Alert severity="info">
              <Typography variant="caption">
                <strong>What happens next:</strong>
                <br />• Campaign will be created and {formData.schedule === "Immediately" ? "start immediately" : "scheduled for " + formData.scheduleDate + " at " + formData.scheduleTime}
                <br />• AI assistant will call {formData.totalContacts} contacts
                <br />• You can monitor progress in the campaigns dashboard
                <br />• You can pause or stop the campaign at any time
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "600px",
          maxHeight: "90vh",
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {isEdit ? "Edit Campaign" : "Create Outbound Campaign"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {!isEdit && `Step ${activeStep + 1} of ${STEPS.length}`}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {!isEdit && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    sx: {
                      "&.Mui-completed": {
                        color: "success.main",
                      },
                    },
                  }}
                >
                  <Typography variant="caption" fontWeight={activeStep === index ? 600 : 400}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}

      <DialogContent dividers sx={{ py: 3 }}>
        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1, justifyContent: "space-between" }}>
        <Box>
          {!isEdit && activeStep > 0 && (
            <CustomButton
              icon={<ArrowBackIcon />}
              iconPosition="left"
              text="Back"
              variant="outlined"
              onClick={handleBack}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <CustomButton
            text="Cancel"
            variant="outlined"
            onClick={handleClose}
          />
          {isEdit || activeStep === STEPS.length - 1 ? (
            <CustomButton
              icon={<SaveIcon />}
              iconPosition="left"
              text={isEdit ? "Update Campaign" : "Launch Campaign"}
              onClick={handleSubmit}
            />
          ) : (
            <CustomButton
              icon={<ArrowForwardIcon />}
              iconPosition="right"
              text="Next"
              onClick={handleNext}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default OutboundCampaignModal;
